import React from 'react'

export interface MetaItem {
  label: string
  value: React.ReactNode
}

export interface StatItem {
  value: string
  label: string
  sub?: string
}

export interface SharedProjectHeroProps {
  title: string
  description: string
  background: string
  award?: string
  stats?: StatItem[]
}

export function SharedProjectHero({
  title,
  description,
  background,
  award,
  stats,
}: SharedProjectHeroProps) {
  const isImage = background.includes('url(')

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .shared-hero-container {
            grid-template-columns: 1fr !important;
            padding: 6rem 1.5rem 3rem !important;
          }
          .shared-hero-stat-wrapper {
            justify-content: flex-start !important;
            margin-top: 2rem !important;
            width: 100% !important;
          }
          .shared-meta-bar {
            grid-template-columns: 1fr !important;
            padding: 1.5rem 0 !important;
            gap: 1.5rem !important;
          }
        }
      `}</style>

      <div
        style={{
          position: 'relative',
          width: '100vw',
          marginLeft: 'calc(-50vw + 50%)',
          minHeight: '480px',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#111111',
          overflow: 'hidden',
          fontFamily: 'var(--font-sans)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundImage: background,
            backgroundSize: isImage ? 'cover' : 'auto',
            backgroundPosition: isImage ? 'center' : 'auto',
            zIndex: 0,
          }}
        />

        {isImage && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              backgroundImage:
                'linear-gradient(90deg, rgba(17,17,17,0.95) 0%, rgba(17,17,17,0.7) 45%, rgba(17,17,17,0.2) 100%)',
              zIndex: 1,
            }}
          />
        )}

        <div
          className="shared-hero-container"
          style={{
            position: 'relative',
            zIndex: 2,
            width: '100%',
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '7rem 2rem 5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '3.5rem',
          }}
        >
          <div style={{ maxWidth: '900px' }}>
            <h1
              style={{
                fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
                fontWeight: 800,
                color: '#ffffff',
                lineHeight: 1.2,
                marginBottom: '1rem',
                wordBreak: 'keep-all',
              }}
            >
              {title}
            </h1>

            <p
              style={{
                fontSize: '1.1rem',
                color: '#d1d5db',
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              {description}

              {award && (
                <>
                  <br />
                  <br />
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: 'rgba(254, 243, 199, 0.15)',
                      color: '#fcd34d',
                      padding: '6px 14px',
                      borderRadius: '99px',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                    }}
                  >
                    {award}
                  </span>
                </>
              )}
            </p>
          </div>

          {stats && stats.length > 0 && (
            <div
              className="shared-hero-stat-wrapper"
              style={{ width: '100%' }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${Math.min(stats.length, 4)}, minmax(0, 1fr))`,
                  gap: '1.25rem',
                  width: '100%',
                }}
              >
                {stats.map((stat, idx) => (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.65)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      borderRadius: '16px',
                      padding: '1.25rem 1.5rem',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '1.85rem',
                        fontWeight: 800,
                        color: stat.value.startsWith('+') || stat.value === '100%' || stat.value === '10 / 10' ? '#4ade80' : stat.value.startsWith('-') ? '#f87171' : '#ffffff',
                        marginBottom: '0.4rem',
                        fontFamily: 'var(--font-sans)',
                        lineHeight: 1.1,
                      }}
                    >
                      {stat.value}
                    </div>
                    <div
                      style={{
                        fontSize: '0.88rem',
                        color: 'rgba(255, 255, 255, 0.9)',
                        lineHeight: 1.4,
                      }}
                    >
                      {stat.label}
                    </div>
                    {stat.sub && (
                      <div
                        style={{
                          fontSize: '0.78rem',
                          color: '#94a3b8',
                          fontWeight: 500,
                          marginTop: '0.35rem',
                          whiteSpace: 'normal',
                          lineHeight: 1.4,
                        }}
                      >
                        {stat.sub}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '150px',
            background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, #ffffff 100%)',
            zIndex: 3,
            pointerEvents: 'none',
          }}
        />
      </div>
    </>
  )
}

export function SharedProjectMetaBar({ items }: { items: MetaItem[] }) {
  return (
    <div
      className="shared-meta-bar"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        padding: '1.25rem 1.5rem',
        marginBottom: '4rem',
        backgroundColor: '#f8fafc',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        alignItems: 'center',
      }}
    >
      {items.map((item, idx) => (
        <div
          key={idx}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.65rem',
            backgroundColor: '#ffffff',
            padding: '0.6rem 1.1rem',
            borderRadius: '12px',
            border: '1px solid #f1f5f9',
            boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
            flex: '1 1 220px',
          }}
        >
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 800,
              color: '#4f46e5',
              backgroundColor: '#eef2ff',
              padding: '4px 10px',
              borderRadius: '8px',
              letterSpacing: '0.05em',
              whiteSpace: 'nowrap',
            }}
          >
            {item.label}
          </span>
          <span
            style={{
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#334155',
              lineHeight: 1.4,
            }}
          >
            {item.value}
          </span>
        </div>
      ))}
    </div>
  )
}
